import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true and disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setPreferences(allPreferences);
    saveCookiePreferences(allPreferences);
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    saveCookiePreferences(preferences);
    setIsVisible(false);
  };

  const handleReject = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setPreferences(minimalPreferences);
    saveCookiePreferences(minimalPreferences);
    setIsVisible(false);
  };

  const saveCookiePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
    }));

    // Here you would typically initialize your analytics/marketing tools
    // based on the user's preferences
    if (prefs.analytics) {
      // Initialize Google Analytics, etc.
      console.log('Analytics cookies enabled');
    }
    if (prefs.marketing) {
      // Initialize marketing pixels, etc.
      console.log('Marketing cookies enabled');
    }
    if (prefs.functional) {
      // Initialize functional cookies
      console.log('Functional cookies enabled');
    }
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Cannot disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const cookieTypes = [
    {
      id: 'necessary',
      title: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function and cannot be disabled.',
      required: true,
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website to improve user experience.',
      required: false,
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      description: 'Used to track visitors and display relevant ads and marketing campaigns.',
      required: false,
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      description: 'Enable enhanced functionality like live chat, forms, and personalized content.',
      required: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50"
        >
          <div className="container mx-auto px-6 py-6">
            {!showSettings ? (
              // Main cookie banner
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-start">
                  <Cookie className="w-8 h-8 text-lion-yellow flex-shrink-0 mt-1 mr-4" />
                  <div>
                    <h3 className="text-lg font-poppins font-semibold text-lion-blue mb-2">
                      We use cookies
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                      By clicking "Accept All", you consent to our use of cookies.{' '}
                      <Link to="/cookie-policy" className="text-lion-blue hover:text-lion-yellow underline">
                        Learn more
                      </Link>
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Customize
                  </button>
                  <button
                    onClick={handleReject}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2 bg-lion-yellow text-lion-blue rounded-lg font-semibold hover:bg-yellow-400 transition-colors duration-200"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            ) : (
              // Cookie settings panel
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-poppins font-semibold text-lion-blue">
                    Cookie Preferences
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="grid gap-4 mb-6">
                  {cookieTypes.map((cookie) => (
                    <div key={cookie.id} className="flex items-start justify-between p-4 bg-lion-gray rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lion-blue mb-1">
                          {cookie.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {cookie.description}
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handlePreferenceChange(cookie.id)}
                          disabled={cookie.required}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            preferences[cookie.id]
                              ? 'bg-lion-yellow'
                              : 'bg-gray-300'
                          } ${cookie.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              preferences[cookie.id] ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={handleReject}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptSelected}
                    className="px-6 py-2 bg-lion-yellow text-lion-blue rounded-lg font-semibold hover:bg-yellow-400 transition-colors duration-200 flex items-center"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;